import { useEffect } from 'react'

import {
  Box,
  Spinner,
  Text,
  Title,
  WizardStep,
  WizardStepRenderParams,
  useOnNext,
} from '@factorialco/deprecated-design-system'
import { A3InnuvaEducationLevelStepDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { EducationalLevelByLegalEntity } from 'modules/payroll_integrations_a3innuva/components/SettingsSteps/EducationalLevel'
import {
  educationLevelsToOptions,
  handleA3innuvaNetworkError,
} from 'modules/payroll_integrations_a3innuva/lib/a3innuvaHelpers'
import { A3InnuvaWizardData } from 'modules/payroll_integrations_a3innuva/lib/types'

const EducationLevelStep = ({
  data,
  onDataChanged,
}: WizardStepRenderParams<A3InnuvaWizardData>) => {
  const session = useCurrentSession()

  const {
    data: educationLevelStepData,
    loading,
    error,
  } = useQuery(A3InnuvaEducationLevelStepDocument, {
    variables: {
      companyId: session.company.id,
      currentUserId: session.access.userId,
      organizationalUnitId: data.answers.organizational_unit_id,
    },
  })

  useEffect(() => {
    if (error) {
      handleA3innuvaNetworkError(error)
    }
  }, [error])

  const legalEntities = educationLevelStepData?.companies.legalEntitiesConnection.nodes ?? []
  const a3innuvaEducationLevels =
    educationLevelStepData?.payrollIntegrationsA3innuva.educationLevels ?? []
  const options = educationLevelsToOptions(a3innuvaEducationLevels)

  useOnNext(() => {
    const educationLevelKeys = Object.keys(data.answers).filter((key) =>
      key.startsWith('education_level_')
    )
    const educationLevelsCount = educationLevelKeys.map((key) => !!data.answers[key]).length

    track(
      trackingEvents.payrollIntegrationsA3Innuva.a3innuvaSettingsNumberOfEducationLevelsMapped,
      {
        company_id: session.company.id,
        access_id: session.access.id,
        education_levels_mapped: educationLevelsCount,
      }
    )
  })

  const onEducationLevelChanged = (educationLevelId: string, value: string) => {
    onDataChanged({
      ...data,
      answers: {
        ...data.answers,
        [`education_level_${educationLevelId}`]: value,
      },
    })
  }

  return loading ? (
    <Spinner />
  ) : (
    <Box gap="s24" width="s600">
      <Title
        title={i18n.t(
          'app_store_v2.app_integrations.payroll_integrations_a3innuva.settings.steps.education_level.title'
        )}
        description={i18n.t(
          'app_store_v2.app_integrations.payroll_integrations_a3innuva.settings.steps.education_level.description'
        )}
      />
      <Text.Normal>
        {i18n.t(
          'app_store_v2.app_integrations.payroll_integrations_a3innuva.settings.steps.education_level.description_2'
        )}
      </Text.Normal>

      {legalEntities.map((legalEntity) => {
        const educationLevels = legalEntity.spanishEducationLevels
        if (!educationLevels || educationLevels.length === 0) return null

        return (
          <EducationalLevelByLegalEntity
            data={data}
            key={`legal_entity_${legalEntity.id}`}
            name={legalEntity.legalName}
            educationLevels={educationLevels}
            options={options}
            onSelect={(educationLevelId, value, _label) =>
              onEducationLevelChanged(educationLevelId, value.toString())
            }
            loading={loading}
          />
        )
      })}
    </Box>
  )
}

export const educationLevelStep = (): WizardStep<A3InnuvaWizardData> => ({
  title: i18n.t(
    'app_store_v2.app_integrations.payroll_integrations_a3innuva.settings.steps.education_level.wizard_title'
  ),
  nextText: i18n.t('common.next'),
  render: (props) => <EducationLevelStep {...props} />,
  isCompleted: (_data) => true,
})
